import {
  FieldError,
  InternalNamePath,
  NamePath,
} from "rc-field-form/lib/interface";

export const normalizeNamePath = (namePath?: NamePath): InternalNamePath => {
  if (namePath === undefined) {
    return [];
  }

  if (Array.isArray(namePath)) {
    return namePath;
  }

  return [namePath];
};

export const namePathEquals = (
  namePathBase?: InternalNamePath,
  namePath?: NamePath
): boolean => {
  if (namePathBase === undefined || namePath === undefined) {
    return false;
  }

  if (!Array.isArray(namePath)) {
    return namePathBase[0] === namePath;
  }

  if (namePathBase.length !== namePath.length) {
    return false;
  }

  return namePathBase.every((nameItem, index) => nameItem === namePath[index]);
};

export const namePathStartsWith = (
  namePathBase?: InternalNamePath,
  namePath?: NamePath
): boolean => {
  if (namePathBase === undefined || namePath === undefined) {
    return false;
  }

  if (typeof namePath === "number") {
    return namePathBase[0] === namePath;
  }

  if (typeof namePath === "string") {
    return (
      typeof namePathBase[0] === "string" &&
      namePathBase[0].startsWith(namePath)
    );
  }

  return namePath.every((nameItem, index) => nameItem === namePathBase[index]);
};

export const namePathEqualsMatcher =
  (namePath: NamePath) => (namePathBase: InternalNamePath) =>
    namePathEquals(namePathBase, namePath);

export const namePathStartsWithMatcher =
  (namePath: NamePath) => (namePathBase: InternalNamePath) =>
    namePathStartsWith(namePathBase, namePath);

export const namePathOrMatcher =
  ({ equals, startsWith }: { equals?: NamePath[]; startsWith?: NamePath[] }) =>
  (namePathBase: InternalNamePath): boolean =>
    equals?.some((namePath) => namePathEquals(namePathBase, namePath)) ||
    startsWith?.some((namePath) =>
      namePathStartsWith(namePathBase, namePath)
    ) ||
    false;

export const mergeNamePaths = (
  namePath1?: NamePath,
  namePath2?: NamePath
): NamePath => [
  ...normalizeNamePath(namePath1),
  ...normalizeNamePath(namePath2),
];

export const subtractNamePaths = (
  namePath1?: NamePath,
  namePath2?: NamePath
): NamePath | undefined => {
  const path1 = normalizeNamePath(namePath1);
  const path2 = normalizeNamePath(namePath2);

  if (!namePathStartsWith(path1, path2)) {
    return namePath2;
  }

  return path1.slice(path2.length);
};

export const getSubfieldErrors = (
  fieldErrors: FieldError[],
  namePath: NamePath
): FieldError[] =>
  fieldErrors.reduce<FieldError[]>((reduced, fieldError) => {
    const subFieldName = subtractNamePaths(fieldError.name, namePath);

    if (!subFieldName) {
      return reduced;
    }

    return [
      ...reduced,
      {
        ...fieldError,
        name: Array.isArray(subFieldName) ? subFieldName : [subFieldName],
      },
    ];
  }, []);
